/**
 * Quality tiers. Every renderer-side cost knob lives here so a new device
 * class only needs a new row, not edits scattered through the systems.
 */
export const TIERS = {
  low: {
    name: 'low',
    pixelRatio: 1.0,
    portalScale: 0.42,      // portal target size, fraction of drawing buffer
    portalDepth: 1,         // recursion levels
    shadows: false,
    shadowSize: 512,
    bloom: true,
    bloomScale: 0.35,
    bloomStrength: 0.42,
    grain: false,
    chromatic: true,
    envRes: 128,
    anisotropy: 2,
    particles: 0.4,
    starCount: 900,
    cityDepth: 2,
    fogFar: 220,
  },
  medium: {
    name: 'medium',
    pixelRatio: 1.5,
    portalScale: 0.6,
    portalDepth: 1,
    shadows: true,
    shadowSize: 1024,
    bloom: true,
    bloomScale: 0.5,
    bloomStrength: 0.5,
    grain: true,
    chromatic: true,
    envRes: 256,
    anisotropy: 4,
    particles: 0.75,
    starCount: 1600,
    cityDepth: 3,
    fogFar: 340,
  },
  high: {
    name: 'high',
    pixelRatio: 2.0,
    portalScale: 0.85,
    portalDepth: 2,
    shadows: true,
    shadowSize: 2048,
    bloom: true,
    bloomScale: 0.6,
    bloomStrength: 0.58,
    grain: true,
    chromatic: true,
    envRes: 512,
    anisotropy: 8,
    particles: 1.0,
    starCount: 2600,
    cityDepth: 4,
    fogFar: 460,
  },
};

export const isTouchDevice = () =>
  (typeof matchMedia === 'function' && matchMedia('(pointer: coarse)').matches) ||
  (navigator.maxTouchPoints ?? 0) > 0;

/**
 * Best-effort device classification. Deliberately conservative: it is much
 * cheaper to start at medium and let the adaptive monitor climb than to open
 * on high and stutter through the first ten seconds of play.
 */
export function detectTier() {
  const mem = navigator.deviceMemory ?? 4;
  const cores = navigator.hardwareConcurrency ?? 4;
  const touch = isTouchDevice();
  const pixels = (window.innerWidth * window.innerHeight) * (window.devicePixelRatio || 1) ** 2;

  if (touch) {
    if (mem <= 3 || cores <= 4 || pixels > 4.5e6) return 'low';
    return 'medium';
  }
  if (mem <= 4 && cores <= 4) return 'medium';
  return 'high';
}

export function resolveTier(setting) {
  if (setting && setting !== 'auto' && TIERS[setting]) return TIERS[setting];
  return TIERS[detectTier()];
}
